import React, { useMemo } from "react";
import PropTypes from "prop-types";
import classNames from "../../../utils/classNames";
import UserCardAvatar from "../UserCardAvatar";
import MessageInlineEditor from "../MessageInlineEditor";
import CommentActionsPanel from "../CommentActionsPanel";
// Reuse message row styles to avoid duplication
import "../MessageRow/index.scss";
import "./index.scss";
import useLocaleDateFormatter from "../../../hooks/useLocaleDateFormatter";
import RichTextRenderer from "../Composer/RichTextRenderer";

export default function CommentRow({
  userId = undefined,
  username = "",
  avatarUrl = undefined,
  createdAt = undefined,
  children = null,
  className = "",
  onEdit = undefined,
  onDelete = undefined,
  canEdit = false,
  isEditing = false,
  onCancelEdit = undefined,
  onSaveEdit = undefined,
  showReactions = true,
}) {
  const displayName = username || "";
  const { formatTime } = useLocaleDateFormatter();

  const createdAtDate = useMemo(() => {
    if (!createdAt) {
      return null;
    }
    const parsed = new Date(createdAt);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }, [createdAt]);

  const renderedTime = createdAtDate ? formatTime(createdAtDate) : "";
  const dateTimeValue = createdAtDate ? createdAtDate.toISOString() : undefined;

  return (
    <div className={classNames("message-row", "comment-row", className)} role="listitem" tabIndex={0} aria-label={displayName ? `${displayName} comment` : "Comment"}>
      <UserCardAvatar
        userId={userId}
        username={displayName || undefined}
        avatarUrl={avatarUrl}
        alt={displayName || "User avatar"}
        className="message-row__avatar"
        size="32px"
        radius="25%"
        enableProfilePreview={Boolean(userId)}
      />
      <div className="message-row__body">
        <div className="message-row__header">
          <strong className="message-row__username">{displayName || "User"}</strong>
          <time className="message-row__time" dateTime={dateTimeValue}>{renderedTime}</time>
        </div>
        <div className={`message-row__content${isEditing ? " message-row__content--editing" : ""}`}>
          {isEditing ? (
            <MessageInlineEditor
              initialHtml={typeof children === "string" ? children : ""}
              onCancel={onCancelEdit}
              onSave={onSaveEdit}
              ariaLabel="Edit comment"
            />
          ) : (
            <div className="comment-row__text">
              {typeof children === "string" ? <RichTextRenderer html={children} /> : children}
            </div>
          )}
        </div>
        {(!isEditing) && (onEdit || onDelete) ? (
          <CommentActionsPanel
            className="comment-row__actions-panel"
            onQuickReact={showReactions ? () => { /* optional hook up later */ } : undefined}
            onMore={() => {}}
            onEdit={onEdit}
            onDelete={onDelete}
            showReactions={showReactions}
          />
        ) : null}
      </div>
    </div>
  );
}

CommentRow.propTypes = {
  userId: PropTypes.string,
  username: PropTypes.string,
  avatarUrl: PropTypes.string,
  createdAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
  children: PropTypes.node,
  className: PropTypes.string,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  canEdit: PropTypes.bool,
  isEditing: PropTypes.bool,
  onCancelEdit: PropTypes.func,
  onSaveEdit: PropTypes.func,
  showReactions: PropTypes.bool,
};
